import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API_BASE_URL } from "../config";
import {
  Plus, Search, Edit2, Trash2, X
} from "lucide-react";
import "./Menu.css";
import Navbar from "../components/Navbar";

function Menu() {
  const navigate = useNavigate();
  const API_URL = `${API_BASE_URL}/menu.php`;
  
  const [menuItems, setMenuItems] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [newItem, setNewItem] = useState({ name: "", category: "Starter", price: "" });

  // Load Menu from Database
  const fetchMenu = async () => {
    try {
      const res = await axios.get(API_URL);
      setMenuItems(res.data);
    } catch (err) {
      console.error("Error fetching menu:", err);
    }
  };

  useEffect(() => {
    fetchMenu();
  }, []);

  const handleSaveItem = async (e) => {
    e.preventDefault();
    try {
      const itemData = editingItem ? { ...newItem, id: editingItem.id } : newItem;
      const res = await axios.post(API_URL, itemData);
      if (res.data.status === "success") {
        fetchMenu();
        closeModal();
      }
    } catch (err) {
      alert("Failed to save item. Check connection.");
    }
  };

  const deleteItem = async (id) => {
    if (window.confirm("Delete this item permanently?")) {
      try {
        // Use POST with an action to bypass server blocks on DELETE method
        const res = await axios.post(API_URL, { action: "delete", id: id });
        if (res.data.status === "success") {
          fetchMenu();
        } else {
          alert("Error: " + res.data.message);
        }
      } catch (err) {
        console.error("Delete error:", err);
        alert("Error deleting item. Check connection.");
      }
    }
  };

  const handleEditClick = (item) => {
    setEditingItem(item);
    setNewItem({ name: item.name, category: item.category, price: item.price });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingItem(null);
    setNewItem({ name: "", category: "Starter", price: "" });
  };

  const filteredItems = menuItems.filter(item => 
    item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="page-layout">
      <Navbar />
      <main className="main-content">
        <div className="menu-container">

        <header className="page-header">
          <div className="header-titles">
            <h1 className="serif-title">Menu Management</h1>
            <p className="subtitle">Add, edit, and manage your menu</p>
          </div>
          <button className="btn-add-menu" onClick={() => setShowModal(true)}>
            <Plus size={18} /> Add Menu Item
          </button>
        </header>

        <div className="search-section">
          <div className="search-wrapper">
            <Search size={18} className="search-icon-menu" />
            <input
              type="text"
              placeholder="Search database items..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        {/* 1. DESKTOP VIEW: Table (Hidden on mobile) */}
        <div className="menu-table-container d-none d-md-block">
          <table className="menu-table">
            <thead>
              <tr>
                <th>Item Name</th>
                <th>Category</th>
                <th>Price</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((item) => (
                <tr key={item.id}>
                  <td className="font-medium">{item.name}</td>
                  <td><span className="badge-category">{item.category}</span></td>
                  <td>₹{item.price}</td>
                  <td className="text-center actions-cell">
                    <button className="icon-btn edit" onClick={() => handleEditClick(item)}><Edit2 size={16} /></button>
                    <button className="icon-btn delete" onClick={() => deleteItem(item.id)}><Trash2 size={16} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* 2. MOBILE VIEW: Cards (Hidden on desktop - Matches your photo) */}
        <div className="mobile-card-list d-md-none">
          {filteredItems.length > 0 ? (
            filteredItems.map((item) => (
              <div className="menu-card-ui" key={item.id}>
                <div className="card-info">
                  <div className="name-row">
                    <span className="item-name">{item.name}</span>
                    <span className="item-category-badge">{item.category}</span>
                  </div>
                  <div className="item-price">₹{parseFloat(item.price).toFixed(2)}</div>
                </div>
                <div className="card-actions">
                  <Edit2 size={18} className="edit-icon-btn" onClick={() => handleEditClick(item)} />
                  <Trash2 size={18} className="delete-icon-btn" onClick={() => deleteItem(item.id)} />
                </div>
              </div>
            ))
          ) : (
            <p className="text-center text-muted mt-4">No items found.</p>
          )}
        </div>

        </div>

        {showModal && (
          <div className="menu-modal-overlay">
            <div className="menu-modal-inner">
              <div className="menu-modal-top">
                <h2>{editingItem ? "Edit Menu Item" : "Add Menu Item"}</h2>
                <X className="menu-modal-close-btn" onClick={closeModal} size={20} />
              </div>
              <form onSubmit={handleSaveItem}>
                <div className="menu-modal-field">
                  <label>Item Name</label>
                  <input required type="text" placeholder="e.g. Paneer Tikka" value={newItem.name} onChange={(e) => setNewItem({ ...newItem, name: e.target.value })} />
                </div>
                <div className="menu-modal-field">
                  <label>Price (₹)</label>
                  <input required type="number" placeholder="e.g. 220" value={newItem.price} onChange={(e) => setNewItem({ ...newItem, price: e.target.value })} />
                </div>
                <div className="menu-modal-field">
                  <label>Category</label>
                  <select value={newItem.category} onChange={(e) => setNewItem({ ...newItem, category: e.target.value })}>
                    <option>Starter</option>
                    <option>Main Course</option>
                    <option>Dessert</option>
                    <option>Beverage</option>
                  </select>
                </div>
                <button type="submit" className="menu-modal-action-btn">
                  {editingItem ? "Save Changes" : "Add Item"}
                </button>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default Menu;